import { Search } from '@mui/icons-material'
import {
    Autocomplete,
    Checkbox,
    InputAdornment,
    TextField,
    Typography,
} from '@mui/material'
import { RosterStatus } from '__generated__/graphql'
import { startCase } from 'lodash'
import { type FC, useEffect, useMemo, useState } from 'react'
import { useRosterSqlSearch } from 'recruiter/hooks'
import { type RosterFilterState } from 'shared/hooks/rosterFilterState'
import { useDebouncedValue } from 'shared/hooks/useDebouncedValue'
import { getPxAsRem } from 'shared/utils'

const statusOptions: RosterStatus[] = Object.values(RosterStatus)

const ghostInputSx = {
    '& .MuiOutlinedInput-root': {
        height: 28,
        fontSize: '0.8125rem',
        paddingLeft: '6px',
        borderRadius: 0,
        '& fieldset': {
            border: 'none',
            borderBottom: '1px solid',
            borderColor: 'divider',
        },
        '&.Mui-focused': {
            '& fieldset': { borderColor: 'primary.main' },
        },
    },
    '& .MuiOutlinedInput-input': { padding: '4px 4px' },
    '& .MuiInputAdornment-root': { marginRight: '2px' },
}

const toolbarSelectSx = {
    '& .MuiOutlinedInput-root': {
        fontSize: '0.75rem',
        py: 0,
        '& fieldset': { borderColor: 'divider' },
        '&:hover fieldset': { borderColor: 'text.secondary' },
    },
}

const toolbarTagSx = {
    fontSize: '0.6875rem',
    color: 'text.secondary',
    px: 0.75,
    py: 0.25,
    borderRadius: 0.5,
    display: 'inline-flex',
    alignItems: 'center',
    whiteSpace: 'nowrap',
}

interface ToolbarFilterSelectProps<T> {
    label: string
    options: T[]
    value: T[]
    onChange: (newValue: T[]) => void
    getOptionLabel?: (option: T) => string
}

function ToolbarFilterSelect<T>({
    label,
    options,
    value,
    onChange,
    getOptionLabel = (opt) => String(opt),
}: ToolbarFilterSelectProps<T>) {
    const [open, setOpen] = useState(false)

    const hasValue = value.length > 0
    const displayLabel = hasValue ? `${label} (${value.length})` : label

    return (
        <Autocomplete
            multiple
            size="small"
            open={open}
            onOpen={() => setOpen(true)}
            onClose={(_, reason) => {
                if (reason !== 'selectOption' && reason !== 'removeOption') {
                    setOpen(false)
                }
            }}
            options={options}
            value={value}
            onChange={(_, newValue) => onChange(newValue)}
            getOptionLabel={getOptionLabel}
            disableCloseOnSelect
            slotProps={{
                popper: { style: { width: 'auto', minWidth: 150 } },
            }}
            sx={toolbarSelectSx}
            renderTags={() => null}
            renderOption={(props, option, { selected }) => (
                <li {...props} key={getOptionLabel(option)}>
                    <Checkbox
                        checked={selected}
                        size="small"
                        sx={{ p: 0, mr: 1 }}
                    />
                    <Typography fontSize="0.75rem">
                        {getOptionLabel(option)}
                    </Typography>
                </li>
            )}
            renderInput={(params) => (
                <TextField
                    {...params}
                    placeholder=""
                    sx={toolbarSelectSx}
                    InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                            <Typography
                                component="span"
                                sx={{
                                    ...toolbarTagSx,
                                    ml: 0.5,
                                    ...(hasValue && {
                                        bgcolor: 'primary.50',
                                        color: 'primary.main',
                                    }),
                                }}
                            >
                                {displayLabel}
                            </Typography>
                        ),
                    }}
                />
            )}
        />
    )
}

export const RosterTableToolbar: FC = () => {
    const { tableState, setTableState } = useRosterSqlSearch()
    const filters = tableState.filters
    const updateFilters = (patch: Partial<RosterFilterState>) =>
        setTableState({ filters: { ...filters, ...patch } })

    const [searchTerm, setSearchTerm] = useState(filters.quickSearch ?? '')
    const debouncedSearchTerm = useDebouncedValue(searchTerm, 500)

    useEffect(() => {
        if (!filters.quickSearch) setSearchTerm('')
    }, [filters.quickSearch])

    useEffect(() => {
        updateFilters({ quickSearch: debouncedSearchTerm || undefined })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearchTerm])

    // Status filter
    const statusesInFilter = useMemo(() => {
        return statusOptions.filter((status) =>
            filters.statuses.includes(status)
        )
    }, [filters.statuses])

    const handleStatusChange = (newValues: RosterStatus[]) => {
        updateFilters({ statuses: newValues })
    }

    return (
        <>
            <TextField
                size="small"
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search..."
                value={searchTerm}
                sx={{ width: getPxAsRem(240), ...ghostInputSx }}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <Search
                                sx={{
                                    fontSize: '1rem',
                                    color: 'text.secondary',
                                }}
                            />
                        </InputAdornment>
                    ),
                }}
            />

            <ToolbarFilterSelect
                label="Status"
                options={statusOptions}
                value={statusesInFilter}
                onChange={handleStatusChange}
                getOptionLabel={(opt) => startCase(opt)}
            />
        </>
    )
}
